/**
 * NotificationsInboxPage — full paginated notification inbox at
 * /dashboard/notifications.
 *
 * Replaces the previous Settings standalone shim that incorrectly showed the
 * notification preferences form here. Preferences remain at
 * Settings → Notifications.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { DashboardHeader } from '@/src/modules/components/Header';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  type ApiNotification,
} from '@/src/infrastructure/lib/api';

// ── Category metadata ─────────────────────────────────────────────────────

type CategoryMeta = { label: string; dot: string; badge: string };

const CATEGORY_META: Record<string, CategoryMeta> = {
  SECURITY:      { label: 'Security',   dot: 'bg-rose-500',    badge: 'bg-rose-50    text-rose-700    border-rose-200'    },
  INVITATION:    { label: 'Invitation', dot: 'bg-violet-500',  badge: 'bg-violet-50  text-violet-700  border-violet-200'  },
  CREDIT:        { label: 'Credit',     dot: 'bg-amber-500',   badge: 'bg-amber-50   text-amber-700   border-amber-200'   },
  BILLING:       { label: 'Billing',    dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  FEEDBACK:      { label: 'Feedback',   dot: 'bg-blue-500',    badge: 'bg-blue-50    text-blue-700    border-blue-200'    },
  SUPPORT:       { label: 'Support',    dot: 'bg-indigo-500',  badge: 'bg-indigo-50  text-indigo-700  border-indigo-200'  },
  ACCOUNT:       { label: 'Account',    dot: 'bg-slate-500',   badge: 'bg-slate-100  text-slate-700   border-slate-200'   },
  RSVP:          { label: 'RSVP',       dot: 'bg-teal-500',    badge: 'bg-teal-50    text-teal-700    border-teal-200'    },
  GUEST_ACTIVITY:{ label: 'Activity',   dot: 'bg-cyan-500',    badge: 'bg-cyan-50    text-cyan-700    border-cyan-200'    },
  REMINDER:      { label: 'Reminder',   dot: 'bg-orange-500',  badge: 'bg-orange-50  text-orange-700  border-orange-200'  },
  MARKETING:     { label: 'Marketing',  dot: 'bg-pink-500',    badge: 'bg-pink-50    text-pink-700    border-pink-200'    },
};

function catMeta(cat: string): CategoryMeta {
  return CATEGORY_META[cat] ?? {
    label: cat,
    dot:   'bg-on-surface-variant/40',
    badge: 'bg-surface-low text-on-surface-variant border-outline-variant/40',
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function fullDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

type NotifTarget =
  | { kind: 'route'; path: string }
  | { kind: 'external'; href: string }
  | { kind: 'modal' };

function resolveTarget(n: ApiNotification): NotifTarget {
  const meta = (n.metadata ?? {}) as Record<string, unknown>;
  const invitationId = typeof meta.invitationId === 'string' ? meta.invitationId : null;
  const feedbackId   = typeof meta.feedbackId   === 'string' ? meta.feedbackId   : null;
  const ticketId     = typeof meta.ticketId     === 'string' ? meta.ticketId     : null;
  const ctaUrl       = typeof meta.ctaUrl       === 'string' ? meta.ctaUrl       : null;

  if (feedbackId)   return { kind: 'route', path: `/dashboard/feedback/${feedbackId}` };
  if (ticketId)     return { kind: 'route', path: `/dashboard/help/tickets/${ticketId}` };
  if (invitationId) return { kind: 'route', path: `/dashboard/invitations?highlight=${invitationId}` };
  if (n.category === 'MARKETING' && ctaUrl) return { kind: 'external', href: ctaUrl };
  // Notifications without a specific linked resource (security alerts, credit
  // events, billing events, etc.) show full detail in a modal — never navigate
  // to Settings, which breaks out of the notification context.
  return { kind: 'modal' };
}

// ── Category filter pills ─────────────────────────────────────────────────

const CATEGORY_FILTERS = [
  'ALL',
  'INVITATION', 'CREDIT', 'BILLING',
  'RSVP', 'FEEDBACK', 'SUPPORT',
  'SECURITY', 'ACCOUNT', 'REMINDER',
  'GUEST_ACTIVITY', 'MARKETING',
] as const;
type CategoryFilter = (typeof CATEGORY_FILTERS)[number];

// ── Main page ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export function NotificationsInboxPage() {
  const navigate = useNavigate();

  const [tab, setTab]               = useState<'all' | 'unread'>('all');
  const [catFilter, setCatFilter]   = useState<CategoryFilter>('ALL');
  const [items, setItems]           = useState<ApiNotification[]>([]);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]           = useState('');
  const [detail, setDetail]         = useState<ApiNotification | null>(null);

  const load = useCallback(async (pageNum: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else { setLoading(true); setError(''); }

    try {
      const result = await getNotifications({
        page: pageNum,
        limit: PAGE_SIZE,
        unreadOnly: tab === 'unread',
      });
      const next = catFilter === 'ALL'
        ? result.items
        : result.items.filter((n) => n.category === catFilter);

      setItems((prev) => append ? [...prev, ...next] : next);
      setTotalPages(result.totalPages);
      setUnreadCount(result.unreadCount);
      setPage(pageNum);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tab, catFilter]);

  useEffect(() => { void load(1, false); }, [load]);

  async function handleClick(n: ApiNotification) {
    if (!n.isRead) {
      setItems((list) => list.map((x) => x.id === n.id ? { ...x, isRead: true } : x));
      setUnreadCount((c) => Math.max(0, c - 1));
      markNotificationRead(n.id).catch(() => undefined);
    }
    const target = resolveTarget(n);
    if (target.kind === 'route') navigate(target.path);
    else if (target.kind === 'external') window.open(target.href, '_blank', 'noopener,noreferrer');
    else setDetail({ ...n, isRead: true });
  }

  async function handleMarkAll() {
    setItems((list) => list.map((x) => ({ ...x, isRead: true })));
    setUnreadCount(0);
    await markAllNotificationsRead().catch(() => undefined);
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    const removed = items.find((x) => x.id === id);
    setItems((list) => list.filter((x) => x.id !== id));
    if (removed && !removed.isRead) setUnreadCount((c) => Math.max(0, c - 1));
    await deleteNotification(id).catch(() => undefined);
  }

  const visibleItems = tab === 'unread' ? items.filter((n) => !n.isRead) : items;
  const hasMore = page < totalPages;

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest pb-20 md:pb-8">
      <DashboardHeader
        title="Notifications"
        description="Your notification history — from the very first one."
      />

      <div className="flex-1 px-4 md:px-8 py-6 max-w-3xl mx-auto w-full space-y-6">
        {/* Sub-header with mark all read */}
        {unreadCount > 0 && (
          <div className="flex items-center justify-between pb-2 border-b border-outline-variant/10">
            <span className="text-xs font-semibold text-on-surface-variant">
              {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </span>
            <button
              onClick={handleMarkAll}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Mark all read
            </button>
          </div>
        )}

        {/* All / Unread tabs */}
        <div className="flex gap-1 p-1 bg-surface-low rounded-xl w-fit">
          {(['all', 'unread'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors capitalize',
                tab === t
                  ? 'bg-surface-lowest text-on-surface shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface',
              )}
            >
              {t}
              {t === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 text-[10px] font-bold text-primary">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Category filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
          {CATEGORY_FILTERS.map((cat) => {
            const label = cat === 'ALL' ? 'All' : catMeta(cat).label;
            const active = catFilter === cat;
            return (
              <button
                key={cat}
                onClick={() => setCatFilter(cat)}
                className={cn(
                  'shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-outline-variant/40 text-on-surface-variant hover:text-on-surface hover:border-outline-variant',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        {loading ? (
          <SkeletonList />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(1, false)} />
        ) : visibleItems.length === 0 ? (
          <EmptyState tab={tab} catFilter={catFilter} />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22 }}
            className="rounded-2xl border border-outline-variant/30 overflow-hidden bg-surface-lowest shadow-sm"
          >
            <AnimatePresence initial={false}>
              {visibleItems.map((n, idx) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  isLast={idx === visibleItems.length - 1 && !hasMore}
                  onClick={() => handleClick(n)}
                  onDelete={(e) => handleDelete(n.id, e)}
                />
              ))}
            </AnimatePresence>

            {hasMore && (
              <div className="border-t border-outline-variant/20 px-4 py-3 text-center bg-surface-lowest">
                <button
                  onClick={() => load(page + 1, true)}
                  disabled={loadingMore}
                  className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
                >
                  {loadingMore ? 'Loading…' : `Load more`}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {detail && (
          <NotificationDetailModal
            notification={detail}
            onClose={() => setDetail(null)}
          />
        )}
      </div>
    </div>
  );
}

// ── Notification row ──────────────────────────────────────────────────────

function NotificationRow({
  notification: n,
  isLast,
  onClick,
  onDelete,
}: {
  notification: ApiNotification;
  isLast: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const meta = catMeta(n.category);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 24 }}
      transition={{ duration: 0.16 }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className={cn(
        'group flex items-start gap-3 px-4 py-4 cursor-pointer transition-colors outline-none',
        'hover:bg-surface-low focus-visible:bg-surface-low',
        !isLast && 'border-b border-outline-variant/20',
        !n.isRead && 'bg-primary/[0.03]',
      )}
    >
      {/* Coloured category bar */}
      <span className={cn('mt-1.5 w-1 h-8 shrink-0 rounded-full', meta.dot)} />

      {/* Unread dot */}
      <span className={cn(
        'mt-2.5 w-2 h-2 shrink-0 rounded-full transition-colors',
        n.isRead ? 'bg-transparent' : 'bg-primary',
      )} />

      {/* Content */}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            'text-sm leading-snug',
            n.isRead ? 'font-medium text-on-surface' : 'font-bold text-on-surface',
          )}>
            {n.title}
          </p>
          <span className="text-[11px] text-on-surface-variant shrink-0 mt-0.5">
            {timeAgo(n.createdAt)}
          </span>
        </div>
        <p className="text-xs text-on-surface-variant line-clamp-2 leading-relaxed">
          {n.message}
        </p>
        <span className={cn(
          'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold mt-1',
          meta.badge,
        )}>
          {meta.label}
        </span>
      </div>

      {/* Delete */}
      <button
        onClick={onDelete}
        aria-label="Delete notification"
        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity self-center shrink-0 p-1 rounded-lg text-on-surface-variant hover:text-destructive hover:bg-destructive/10"
      >
        <Icons.Trash size={15} />
      </button>
    </motion.div>
  );
}

// ── Detail modal ──────────────────────────────────────────────────────────

function NotificationDetailModal({
  notification: n,
  onClose,
}: {
  notification: ApiNotification;
  onClose: () => void;
}) {
  const meta = catMeta(n.category);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.18 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface-lowest rounded-2xl border border-outline-variant/40 shadow-xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-outline-variant/30">
          <div className="min-w-0 space-y-1.5">
            <p className="text-base font-bold text-on-surface leading-snug">{n.title}</p>
            <div className="flex items-center gap-2">
              <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', meta.badge)}>
                {meta.label}
              </span>
              <span className="text-[11px] text-on-surface-variant">{fullDateTime(n.createdAt)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-on-surface-variant hover:text-on-surface p-1 shrink-0"
          >
            <Icons.X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-on-surface whitespace-pre-wrap leading-relaxed">{n.message}</p>
        </div>
      </motion.div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────

function SkeletonList() {
  return (
    <div className="rounded-2xl border border-outline-variant/30 overflow-hidden bg-surface-lowest divide-y divide-outline-variant/20">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-start gap-3 px-4 py-4 animate-pulse">
          <span className="mt-1.5 w-1 h-8 shrink-0 rounded-full bg-outline-variant/30" />
          <span className="mt-2.5 w-2 h-2 shrink-0 rounded-full bg-outline-variant/30" />
          <div className="flex-1 space-y-2 py-0.5">
            <div className="flex items-center justify-between gap-2">
              <div className="h-3.5 bg-outline-variant/30 rounded-full w-2/5" />
              <div className="h-3 bg-outline-variant/20 rounded-full w-14" />
            </div>
            <div className="h-3 bg-outline-variant/20 rounded-full w-4/5" />
            <div className="h-4 bg-outline-variant/15 rounded-full w-16 mt-1" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Empty / error states ──────────────────────────────────────────────────

function EmptyState({ tab, catFilter }: { tab: string; catFilter: string }) {
  const isFiltered = tab === 'unread' || catFilter !== 'ALL';
  return (
    <div className="rounded-2xl border border-dashed border-outline-variant/40 px-6 py-16 text-center">
      <div className="w-12 h-12 rounded-full bg-surface-low flex items-center justify-center mx-auto mb-4">
        <Icons.Bell size={22} className="text-on-surface-variant" />
      </div>
      <p className="text-sm font-semibold text-on-surface mb-1">
        {isFiltered ? 'No notifications here' : "You're all caught up"}
      </p>
      <p className="text-xs text-on-surface-variant">
        {isFiltered
          ? 'Try changing the filter to see other notifications.'
          : 'New notifications will appear here as you use InviteCloud.'}
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-rose-300/50 bg-rose-50/60 px-5 py-6 text-center space-y-2">
      <p className="text-sm font-semibold text-rose-700">{message}</p>
      <button onClick={onRetry} className="text-xs font-medium text-rose-600 hover:underline">
        Try again
      </button>
    </div>
  );
}
